import { generateId } from "@repo/core";
import type { RecurringFrequency, Transaction } from "./schema";

export interface RecurringDraft {
  date: string;
  type: Transaction["type"];
  amount: number;
  categoryId: string;
  accountId: string;
  person: string;
  notes?: string;
  frequency: RecurringFrequency;
  intervalDays?: number;
  /** How many future occurrences to materialize (including the first). Default 12 monthly / 26 weekly. */
  count?: number;
  endDate?: string;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso + "T12:00:00");
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

function addMonths(iso: string, months: number): string {
  const d = new Date(iso + "T12:00:00");
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
}

export function nextOccurrenceDate(
  iso: string,
  frequency: RecurringFrequency,
  intervalDays = 14
): string {
  if (frequency === "weekly") return addDays(iso, 7);
  if (frequency === "custom") return addDays(iso, Math.max(1, intervalDays));
  return addMonths(iso, 1);
}

export function defaultOccurrenceCount(frequency: RecurringFrequency): number {
  if (frequency === "weekly") return 26;
  if (frequency === "custom") return 24;
  return 12;
}

/** Build materialized transaction rows for a new recurring series. */
export function buildRecurringSeries(draft: RecurringDraft): Transaction[] {
  const seriesId = generateId();
  const count = draft.count ?? defaultOccurrenceCount(draft.frequency);
  const rows: Transaction[] = [];
  let date = draft.date;

  for (let i = 0; i < count; i++) {
    if (draft.endDate && date > draft.endDate) break;
    rows.push({
      id: generateId(),
      date,
      type: draft.type,
      amount: draft.amount,
      categoryId: draft.categoryId,
      accountId: draft.accountId,
      person: draft.person,
      notes: draft.notes,
      seriesId,
      recurringFrequency: draft.frequency,
      recurringIntervalDays: draft.frequency === "custom" ? draft.intervalDays : undefined,
      recurringEndDate: draft.endDate,
    });
    date = nextOccurrenceDate(date, draft.frequency, draft.intervalDays);
  }
  return rows;
}

/** Detach one occurrence from the series (edit this one only). */
export function detachOccurrence(tx: Transaction): Partial<Transaction> {
  return {
    seriesId: undefined,
    recurringFrequency: undefined,
    recurringIntervalDays: undefined,
    recurringEndDate: undefined,
    skipped: false,
  };
}

/** Apply field updates to every non-skipped member of a series (except date stays per-row). */
export function seriesUpdatePayload(
  fields: Pick<Transaction, "amount" | "type" | "categoryId" | "accountId" | "person" | "notes">
): Partial<Transaction> {
  return { ...fields };
}
